import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import CustomSelect from './common/CustomSelect';
import '../styles/Modal.css';

function EditUserModal({ isOpen, onClose, onSubmit, user }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'member',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        role: user.role || 'member',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    setError(''); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSubmit(user.id, formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className={`modal-container modal-large ${loading ? 'modal-loading' : ''}`}>
        <div className="modal-header">
          <h3>Edit User</h3>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-with-indicator">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className={loading ? 'input-disabled' : ''}
                  />
                  <span className="required-indicator">*</span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="input-with-indicator">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className={loading ? 'input-disabled' : ''}
                  />
                  <span className="required-indicator">*</span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-with-indicator">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className={loading ? 'input-disabled' : ''}
                />
                <span className="required-indicator">*</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="role">User Role</label>
              <div className="input-with-indicator">
                <CustomSelect
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={loading}
                  options={[
                    { value: 'member', label: 'Member', icon: '👤' },
                    { value: 'admin', label: 'Administrator', icon: '🛡️' }
                  ]}
                />
                <span className="required-indicator">*</span>
              </div>
              <small className="form-hint">
                Admins have full application access. Members can only access their household.
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Active User</span>
              </label>
              <small className="form-hint">
                Inactive users cannot log in to the application.
              </small>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary btn-with-loader"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="btn-spinner" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
        
        {/* Loading overlay */}
        {loading && (
          <div className="modal-loading-overlay">
            <Loader2 size={24} className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
}

export default EditUserModal;
